import { useEffect, useRef, useState } from "react"
import { Popover } from "@headlessui/react"
import cc from "classcat"

export function NavBar({
  sections,
  theme = "yellow",
  compact = false,
}: {
  sections: { id: string; title: string; dotClassName?: string }[]
  theme?: "yellow" | "red" | "green" | "dark"
  compact?: boolean
}) {
  const navBarRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    function updateActiveIndex() {
      let newActiveIndex = -1
      if (!sections || sections.length === 0) {
        setActiveIndex(newActiveIndex)
        return
      }

      const elements = sections.map(({ id }) => document.getElementById(id))

      if (!navBarRef.current) {
        return
      }

      const bodyRect = document.body.getBoundingClientRect()
      const offset = bodyRect.top + navBarRef.current.offsetHeight + 1

      for (let index = 0; index < elements.length; index += 1) {
        const element = elements[index]
        if (!element) {
          continue
        }
        if (window.scrollY >= element.getBoundingClientRect().top - offset) {
          newActiveIndex = index
        } else {
          break
        }
      }

      setActiveIndex(newActiveIndex)
    }

    updateActiveIndex()

    window.addEventListener("resize", updateActiveIndex)
    window.addEventListener("scroll", updateActiveIndex, { passive: true })

    return () => {
      window.removeEventListener("resize", updateActiveIndex)
      window.removeEventListener("scroll", updateActiveIndex)
    }
  }, [sections])

  return (
    <div ref={navBarRef} className="sticky top-0 z-30">
      <Popover className="sm:hidden">
        {({ open }) => (
          <>
            <div
              className={cc([
                "relative flex items-center px-4",
                compact ? "py-2.5" : "py-3",
                {
                  "shadow-sm [@supports(backdrop-filter:blur(0))]:backdrop-blur":
                    !open,
                  "bg-kkm-yellow/50": theme === "yellow",
                  "bg-kkm-red/80": theme === "red",
                  "bg-kkm-green/50": theme === "green",
                  "bg-zinc-900/95": theme === "dark",
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-yellow/50":
                    theme === "yellow" && !open,
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-red/80":
                    theme === "red" && !open,
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-green/50":
                    theme === "green" && !open,
                  "[@supports(backdrop-filter:blur(0))]:bg-zinc-900/95":
                    theme === "dark" && !open,
                },
              ])}
            >
              {!open && (
                <span
                  className={cc([
                    "inline-flex items-center gap-2 text-base",
                    {
                      "font-medium text-zinc-900": ["yellow", "light"].includes(
                        theme,
                      ),
                      "font-bold text-zinc-100": ["green", "red", "dark"].includes(
                        theme,
                      ),
                    },
                  ])}
                >
                  {sections[Math.max(0, activeIndex)]?.dotClassName && (
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${sections[Math.max(0, activeIndex)].dotClassName}`}
                      aria-hidden="true"
                    />
                  )}
                  {sections[Math.max(0, activeIndex)]?.title || ""}
                </span>
              )}
              <Popover.Button
                className={cc([
                  "-mr-1 ml-auto flex h-8 w-8 items-center justify-center",
                  { "relative z-10": open },
                ])}
              >
                {!open && <span className="absolute inset-0" />}
                <span className="sr-only">Toggle navigation menu</span>
                <svg
                  aria-hidden="true"
                  className={cc(["h-6 w-6", { "stroke-slate-700": theme !== "dark", "stroke-zinc-300": theme === "dark" }])}
                  fill="none"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path
                    d={
                      open
                        ? "M17 7 7 17M7 7l10 10"
                        : "m15 16-3 3-3-3M15 8l-3-3-3 3"
                    }
                  />
                </svg>
              </Popover.Button>
            </div>
            <Popover.Panel
              className={cc([
                "absolute inset-x-0 top-0 py-2 shadow-sm [@supports(backdrop-filter:blur(0))]:backdrop-blur",
                {
                  "bg-white/95": theme !== "dark",
                  "bg-zinc-900/95": theme === "dark",
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-green/50":
                    theme === "green",
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-yellow/50":
                    theme === "yellow",
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-red/80":
                    theme === "red",
                  "[@supports(backdrop-filter:blur(0))]:bg-zinc-900/95":
                    theme === "dark",
                },
              ])}
            >
              {({ close }) =>
                sections
                  .filter((section) => section)
                  .map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center px-4 py-2"
                      onClick={() => close()}
                    >
                      <span
                        className={cc([
                          "inline-flex items-center gap-2 text-base",
                          {
                            "font-medium text-zinc-900": [
                              "yellow",
                              "light",
                            ].includes(theme),
                            "font-bold text-zinc-100": [
                              "green",
                              "red",
                              "dark",
                            ].includes(theme),
                          },
                        ])}
                      >
                        {section.dotClassName && (
                          <span
                            className={`h-2 w-2 shrink-0 rounded-full ${section.dotClassName}`}
                            aria-hidden="true"
                          />
                        )}
                        {section.title}
                      </span>
                    </a>
                  ))
              }
            </Popover.Panel>
            <div
              className={cc([
                "absolute inset-x-0 bottom-full z-10",
                {
                  "bg-white": theme !== "dark",
                  "bg-zinc-900": theme === "dark",
                },
              ])}
            />
          </>
        )}
      </Popover>
      <div
        className={cc([
          "hidden sm:flex sm:justify-center sm:[@supports(backdrop-filter:blur(0))]:backdrop-blur",
          compact ? "sm:h-20" : "sm:h-24",
          {
            "sm:bg-kkm-yellow/60 sm:[@supports(backdrop-filter:blur(0))]:bg-kkm-yellow/60":
              theme === "yellow",
            "sm:bg-kkm-red/60 sm:[@supports(backdrop-filter:blur(0))]:bg-kkm-red/60":
              theme === "red",
            "sm:bg-kkm-green/60 sm:[@supports(backdrop-filter:blur(0))]:bg-kkm-green/60":
              theme === "green",
            "sm:bg-zinc-900/95 sm:[@supports(backdrop-filter:blur(0))]:bg-zinc-900/95":
              theme === "dark",
          },
        ])}
      >
        <ol className={cc(["-mb-[2px] ml-0 grid auto-cols-[minmax(0,19rem)] grid-flow-col text-sm font-medium [counter-reset:section]", { "text-slate-900": theme !== "dark", "text-zinc-300": theme === "dark" }])}>
          {sections
            .filter((section) => section)
            .map((section, index) => (
              <li key={section.id} className="flex">
                <a
                  href={`#${section.id}`}
                  className={cc([
                    "flex w-full flex-col items-center justify-center border-b-2 px-2 text-center before:font-mono before:text-sm before:content-none",
                    compact ? "before:mb-1" : "before:mb-2",
                    {
                      "border-black bg-white/50 text-slate-900 before:text-white/40":
                        index === activeIndex && theme !== "dark",
                      "border-white/80 bg-white/10 text-white":
                        index === activeIndex && theme === "dark",
                      "border-transparent before:text-slate-500 hover:before:text-slate-900":
                        index !== activeIndex && theme !== "dark",
                      "border-transparent text-zinc-400 hover:text-zinc-100":
                        index !== activeIndex && theme === "dark",
                      "hover:bg-kkm-yellow/40": theme === "yellow",
                      "hover:bg-kkm-red/40": theme === "red",
                      "hover:bg-kkm-green/40": theme === "green",
                      "hover:bg-white/10": theme === "dark",
                    },
                  ])}
                >
                  <span className="inline-flex items-center gap-2">
                    {section.dotClassName && (
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${section.dotClassName}`}
                        aria-hidden="true"
                      />
                    )}
                    {section.title}
                  </span>
                </a>
              </li>
            ))}
        </ol>
      </div>
    </div>
  )
}
